// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { LangLink as Link } from "@/components/LangLink";
import { PageHero, PageShell } from "@/components/PageShell";
import { ImageTextSection } from "@/components/ImageTextSection";
import { FeatureGrid } from "@/components/FeatureGrid";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import { Cloud, Cog, Droplet, Shield, Sparkles, Wind, Factory } from "lucide-react";

// Images
import heroImage from "@/assets/hero-contact.avif";
import pururucaImage1 from "@/assets/arion.png";
import pururucaImage2 from "@/assets/pururuca2.jpg";

export const Route = createFileRoute("/$lang/products/catalyst")({
  head: () => ({
    meta: [
      { title: i18n.t("products/catalyst:metaTitle", "Hydrogen Peroxide Catalysts — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/catalyst:metaDesc",
          "BIZU Space's transition-metal hydrogen peroxide catalyst quickly decomposes HTP into high-temperature steam and oxygen for green propulsion.",
        ),
      },
      { property: "og:title", content: i18n.t("products/catalyst:metaTitle", "Hydrogen Peroxide Catalysts — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/catalyst:metaOgDesc",
          "High-performance catalyst beds and heterogeneous catalytic systems developed in partnership with UNIVAP for HTP decomposition.",
        ),
      },
      { property: "og:url", content: "/products/catalyst" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/catalyst" }],
  }),
  component: Catalyst,
});

function Catalyst() {
  const { t } = useTranslation("products/catalyst");

  const spinOffApplications = [
    {
      title: t("applications.wastewaterTitle", "Wastewater Treatment"),
      description: t("applications.wastewaterDesc",
        "Catalyst technologies for peroxide-based advanced oxidation processes in industrial effluent treatment.",
      ),
      icon: Droplet,
    },
    {
      title: t("applications.oxygenTitle", "On-Demand Oxygen Generation"),
      description: t("applications.oxygenDesc",
        "Controlled decomposition systems for oxygen generation and process-gas supply.",
      ),
      icon: Cloud,
    },
    {
      title: t("applications.safetyTitle", "Confined-Environment Safety"),
      description: t("applications.safetyDesc",
        "Potential application in the catalytic removal of hazardous gases in enclosed and safety-critical environments.",
      ),
      icon: Shield,
    },
    {
      title: t("applications.purificationTitle", "Gas Treatment & Purification"),
      description: t("applications.purificationDesc",
        "Solutions for the decomposition and control of reactive species in industrial gas streams.",
      ),
      icon: Wind,
    },
    {
      title: t("applications.sanitizationTitle", "Sanitization & Decontamination"),
      description: t("applications.sanitizationDesc",
        "Catalyst applications for residual peroxide abatement in sterilization and decontamination systems.",
      ),
      icon: Sparkles,
    },
    {
      title: t("applications.reactorsTitle", "Compact Industrial Reactors"),
      description: t("applications.reactorsDesc",
        "Expertise in catalyst-bed design and flow engineering for compact process reactors and gas-generation units.",
      ),
      icon: Cog,
    },
    {
      title: t("applications.inhouseTitle", "In-house produced"),
      description: t("applications.inhouseDesc",
        "BIZU Space and UNIVAP (Laboratório de Catálise Heterogênea - LabCat) are developing in partnership the space grade hydrogen peroxide catalyst for propulsion purposes.",
      ),
      icon: Factory,
      fullWidth: true,
    },
  ];

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        title={t("heroTitle", "Hydrogen Peroxide Catalysts")}
        description={t("heroDesc",
          "BIZU Space’s hydrogen peroxide catalyst is an innovative solution based on transition metals. It is used to quickly decompose H2O2 into water and oxygen at high temperatures.",
        )}
        image={heroImage}
      />

      <ImageTextSection
        title={t("propulsionTitle", "Non-toxic propulsion")}
        image={pururucaImage1}
        imagePosition="right"
        description={t("propulsionDesc",
          "With the recent increase in the commercialization of space, the demand for “movement” in space is rising. This necessity means propulsion. For a quick and powerful maneuver, chemical propulsion is needed. In these thrusters, the exothermic decomposition and expansion of decomposed products provide the thrust for orbit control and high precision maneuvers. For a non-toxic solution, alternative to hydrazine, BIZU Space develops catalysts for propulsion units based on decomposed hydrogen peroxide – monopropellant and bipropellant mode.",
        )}
      />

      {/* KEY CAPABILITIES */}
      <section className="relative overflow-hidden bg-black py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 max-w-4xl">
            <span className="section-tag">{t("capabilitiesTitle", "Key Capabilities")}</span>

            <h2 className="display mt-5 text-4xl uppercase md:text-6xl">
              {t("capabilitiesSub", "Performance, reliability and mission flexibility")}
            </h2>
          </Reveal>
          <FeatureGrid items={spinOffApplications} />
        </div>
      </section>

      <ImageTextSection
        title={t("decomposedTitle", "Decomposed HTP in propulsion units and gas generators")}
        image={pururucaImage2}
        description={t("decomposedDesc",
          "The catalytic decomposition of HTP produces high-temperature gases, mainly steam and oxygen. This makes HTP a highly versatile, non-toxic green propellant for applications such as rockets, satellite propulsion, and gas-generation systems for turbopump drive. Catalyst-based operation also enables both monopropellant and bipropellant modes in HTP-powered engines.",
        )}
      />

      <CtaSection
        image={heroImage}
        title={t("ctaTitle", "NEED H2O2 CATALYST?")}
        subtitle={t("ctaSubtitle",
          "BIZU Space offers H2O2 catalyst for experimental use and propulsion systems, ensuring reliability and consistent quality.",
        )}
        buttonText={t("ctaBtn", "Get in touch")}
        href="/contact"
      />
    </PageShell>
  );
}
